import React, { useState, useEffect, useRef } from "react";
import { IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./MultiSelectDropdown.css";

interface Option {
  label?: string | undefined;
  value?: string | number | undefined;
  accessTo?: string[] | undefined;
}

interface MultiSelectDropdownProps {
  options: Option[];
  label: string;
  value: (string | number)[];
  onChange: (selected: (string | number | undefined)[]) => void;
  optional?: boolean;
  storeLabels?: boolean;
  showSelectAll?: boolean;
  showClearAll?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  label,
  value = [],
  optional = false,
  onChange,
  storeLabels = false,
  showSelectAll = true,
  showClearAll = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] =
    useState<(string | number | undefined)[]>(value);
  const [searchText, setSearchText] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Synchronize state with props
  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCheckboxChange = (optionValue: string | number | undefined, optionLabel?: string) => {
    const valueToStore = storeLabels ? optionLabel : optionValue;
    const updatedOptions = selectedOptions.includes(valueToStore)
      ? selectedOptions.filter((val) => val !== valueToStore)
      : [...selectedOptions, valueToStore];
    setSelectedOptions(updatedOptions);
    onChange(updatedOptions);
  };

  const handleSelectAll = () => {
    const allValues = storeLabels 
      ? options.map((option) => option.label)
      : options.map((option) => option.value);
    const newSelectedOptions =
      selectedOptions.length === options.length ? [] : allValues;
    setSelectedOptions(newSelectedOptions);
    onChange(newSelectedOptions);
  };

  const handleDropdownToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOptions([]);
    onChange([]);
  };

  // Filter options
  const filteredOptions = options.filter((option: any) =>
    option?.label?.toLowerCase().includes(searchText?.toLowerCase())
  );

  // Get display text for selected options
  const getDisplayText = () => {
    if (selectedOptions.length === 0) return "Please select";
    if (storeLabels) {
      return selectedOptions?.join(', ');
    } else {
      return options.filter((option) => {return selectedOptions?.includes(option.value)}).map((option) => option?.label)?.join(', ');
    }
  };

  // Check if option is selected
  const isOptionSelected = (option: Option) => {
    const valueToCheck = storeLabels ? option.label : option.value;
    return selectedOptions.includes(valueToCheck);
  };

  return (
    <div ref={dropdownRef} className="multi-select-dropdown-container mb-2">
      <p className="multi-select-dropdown-label mb-10 text-start search-label text-muted">
        {label} {optional && <span style={{color:'red'}} className="optional-asterisk">*</span>}
      </p>
      <div
        className={`multi-select-dropdown-header ${isOpen ? 'open' : ''}`}
        onClick={handleDropdownToggle}
      >
        <Tooltip
          title={selectedOptions?.length > 2 ? getDisplayText() : ''}
          placement="bottom-start"
          componentsProps={{
            tooltip: {
              className: 'custom-multiselect-tooltip'
            }
          }}
        >
          <span
            className={`
              ${selectedOptions.length > 0
                ? "placeholder-style-selected"
                : "placeholder-style"}
              ${selectedOptions?.length > 2 
                ? 'display-text-overflow'
                : ''
              }
            `}
          >
            {getDisplayText()}
          </span>
        </Tooltip>
        <div className="multi-select-dropdown-header-controls">
          {showClearAll && selectedOptions.length > 0 && (
            <IconButton
              size="small"
              onClick={handleClearAll}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          <div className="multi-select-dropdown-arrow-container">
            <div className="multi-select-dropdown-triangle"></div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="multi-select-dropdown-options">
          {/* Search */}
          {options.length > 0 ? (
            <div className="multi-select-dropdown-search">
              <input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="multi-select-dropdown-search-input"
              />
            </div>
          ) : (
            <div className="multi-select-dropdown-search">
              <text className="multi-select-dropdown-no-options">No {label} Available</text>
            </div>
          )}
          {/* Select All - only if showSelectAll is true */}
          {showSelectAll && options.length > 1 && filteredOptions.length > 1 && (
            <div className="multi-select-dropdown-select-all" onClick={handleSelectAll}>
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={selectedOptions.length === options.length}
                onChange={handleSelectAll}
              />
              <label className="multi-select-dropdown-select-all-label">
                Select All
              </label>
            </div>
          )}
          {/* Options */}
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`multi-select-dropdown-option ${
                isOptionSelected(option) ? "selected" : ""
              }`}
              onClick={() => handleCheckboxChange(option.value, option.label)}
            >
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={isOptionSelected(option)}
                onChange={() => handleCheckboxChange(option.value, option.label)}
              />
              <label className="multi-select-dropdown-option-label">
                {option.label}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
